import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { get, put } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import InfoCard from "mobile/components/InfoCard";
import { IosCard, IosPage } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const formatDate = (v) => (v ? String(v).slice(0, 10) : "—");

const formatMoney = (value) => {
  const n = value == null ? 0 : Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

export default function PurchaseOrderDetailMobile() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState({ status: "draft", expectedDeliveryDate: "", receivedDate: "", notes: "" });

  const { data, isLoading, error } = useQuery({
    queryKey: ["m-purchase-order", documentId],
    queryFn: () =>
      get(`/api/purchase-orders/${documentId}`, {
        publicationState: "live",
        populate: {
          vendor: true,
          items: { populate: { inventoryItem: true } },
          receipts: { populate: { lines: { populate: { inventoryItem: true } } } },
        },
      }),
    enabled: !!documentId,
    staleTime: 10_000,
  });

  const po = useMemo(() => (data?.data ? normalize(data.data) : null), [data]);
  const vendor = useMemo(() => {
    const vRaw = po?.vendor?.data || po?.vendor || null;
    return vRaw ? normalize(vRaw) : null;
  }, [po]);
  const items = useMemo(() => {
    const raw = po?.items?.data || po?.items || [];
    return raw.map(normalize);
  }, [po]);
  const receipts = useMemo(() => {
    const raw = po?.receipts?.data || po?.receipts || [];
    return raw.map(normalize);
  }, [po]);

  useEffect(() => {
    if (!po) return;
    setDraft({
      status: po.status || "draft",
      expectedDeliveryDate: po.expectedDeliveryDate ? String(po.expectedDeliveryDate).slice(0, 10) : "",
      receivedDate: po.receivedDate ? String(po.receivedDate).slice(0, 10) : "",
      notes: po.notes || "",
    });
  }, [po, editOpen]);

  const saveMutation = useMutation({
    mutationFn: () =>
      put(`/api/purchase-orders/${po?.documentId || documentId}`, {
        data: {
          status: draft.status,
          expectedDeliveryDate: draft.expectedDeliveryDate || null,
          receivedDate: draft.receivedDate || null,
          notes: draft.notes || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-purchase-order", documentId] });
      setEditOpen(false);
    },
  });

  if (isLoading) return <LinearProgress />;
  if (error || !po) {
    return (
      <IosPage>
        <Typography variant="subtitle1" fontWeight={900}>
          Purchase order not found
        </Typography>
      </IosPage>
    );
  }

  const totalMoney = po.totalWithTax != null ? formatMoney(po.totalWithTax) : formatMoney(po.total);

  return (
    <IosPage>
      <MobileTopBar
        title="Purchase Order"
        subtitle={po.purchaseOrderNumber || ""}
        onBack={() => navigate("/m/purchase-orders")}
        right={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip size="small" variant="outlined" label={po.status || "draft"} />
            <IconButton aria-label="Edit purchase order" size="small" onClick={() => setEditOpen(true)}>
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Stack>
        }
      />

      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <InfoCard
          title="Summary"
          rows={[
            { label: "PO #", value: po.purchaseOrderNumber || "—" },
            { label: "Status", value: po.status || "draft" },
            { label: "Vendor", value: vendor?.name || "—" },
            { label: "Expected", value: formatDate(po.expectedDeliveryDate) },
            { label: "Received", value: formatDate(po.receivedDate) },
            { label: "Items", value: items.length },
          ]}
        />

        <InfoCard
          title="Totals"
          rows={[
            { label: "Subtotal", value: formatMoney(po.total) },
            { label: "Taxable", value: po.isTaxable ? "Yes" : "No" },
            { label: "Tax %", value: po.taxRatePercent != null && po.taxRatePercent !== "" ? `${po.taxRatePercent}%` : "—" },
            { label: "Tax", value: po.taxAmount != null ? formatMoney(po.taxAmount) : "—" },
            { label: "Total", value: totalMoney },
          ]}
        />

        {po.notes ? (
          <IosCard>
              <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                {po.notes}
              </Typography>
          </IosCard>
        ) : null}

        <IosCard>
            <Typography variant="subtitle2" fontWeight={900}>
              Items
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={0.75}>
              {items.map((it) => {
                const invRaw = it?.inventoryItem?.data || it?.inventoryItem || null;
                const inv = invRaw ? normalize(invRaw) : null;
                return (
                  <IosCard key={it.documentId || it.id} sx={{ minHeight: 0, p: 1.35, boxShadow: "none" }}>
                      <Typography variant="body2" fontWeight={900} noWrap>
                        {inv?.sku || inv?.name || "Item"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          mt: 0.25,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          wordBreak: "break-word",
                        }}
                      >
                        Qty {String(it.quantity ?? "—")} • Unit {formatMoney(it.unitCost)} • Line{" "}
                        {it.lineTotal != null ? formatMoney(it.lineTotal) : "—"}
                      </Typography>
                      {it.notes ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            mt: 0.75,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {String(it.notes)}
                        </Typography>
                      ) : null}
                  </IosCard>
                );
              })}
              {!items.length ? (
                <Typography variant="body2" color="text.secondary">
                  No items yet.
                </Typography>
              ) : null}
            </Stack>
        </IosCard>

        <IosCard>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={900}>
                Receipts
              </Typography>
              <Chip size="small" variant="outlined" label={`${receipts.length}`} />
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={0.75}>
              {receipts.map((r) => {
                const lines = (r?.lines?.data || r?.lines || []).map(normalize);
                return (
                  <IosCard key={r.documentId || r.id} sx={{ minHeight: 0, p: 1.35, boxShadow: "none" }}>
                      <Stack direction="row" justifyContent="space-between" spacing={0.75} alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={900} noWrap>
                            {r.ticketNumber || "Receipt"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {r.status || "—"} • {r.receivedAt ? formatDate(r.receivedAt) : "—"} • {lines.length} lines
                          </Typography>
                        </Box>
                        <Chip size="small" variant="outlined" label={r.status || "draft"} />
                      </Stack>
                  </IosCard>
                );
              })}
              {!receipts.length ? (
                <Typography variant="body2" color="text.secondary">
                  No receipts yet.
                </Typography>
              ) : null}
            </Stack>
        </IosCard>
      </Stack>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullScreen>
        <IosPage>
          <MobileTopBar title="Edit PO" subtitle={po.purchaseOrderNumber || ""} onBack={() => setEditOpen(false)} />
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={draft.status} onChange={(e) => setDraft((p) => ({ ...p, status: e.target.value }))}>
                {["draft", "open", "received", "cancelled"].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Expected delivery date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={draft.expectedDeliveryDate}
              onChange={(e) => setDraft((p) => ({ ...p, expectedDeliveryDate: e.target.value }))}
            />
            <TextField
              label="Received date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={draft.receivedDate}
              onChange={(e) => setDraft((p) => ({ ...p, receivedDate: e.target.value }))}
            />
            <TextField label="Notes" multiline minRows={4} value={draft.notes} onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))} />
            <Stack direction="row" spacing={0.75} sx={{ pt: 0.5 }}>
              <Button variant="outlined" fullWidth onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button variant="contained" fullWidth disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                Save
              </Button>
            </Stack>
            {saveMutation.isPending ? <LinearProgress /> : null}
          </Stack>
        </IosPage>
      </Dialog>
    </IosPage>
  );
}
